import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { fetchContactInfo } from '../lib/sanity';
import type { ContactInfo } from '../lib/types';
import { defaultContactInfo } from '../lib/defaultContent';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [contactInfo, setContactInfo] = useState<ContactInfo>(defaultContactInfo);

  useEffect(() => {
    fetchContactInfo().then((data) => {
      if (data) setContactInfo(data);
    });
  }, []);

  const socialIconSrc: Record<string, string> = {
    x: '/icons/x.svg',
    tiktok: '/icons/tiktok.svg',
    instagram: '/icons/instagram.svg',
    whatsapp: '/icons/whatsapp.svg',
  };

  return (
    <footer className="bg-primary-black text-cream-50 pt-16 pb-8">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="relative inline-block mb-4 px-6 py-3">
              <div className="absolute inset-0 rounded-full blur-2xl" style={{ background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.15) 55%, transparent 100%)' }} />
              <img
                src="/icons/revianclean.png"
                alt="Revian Supplies"
                className="relative w-28 h-auto"
              />
            </div>
            <p className="text-cream-100 mb-4 text-sm leading-relaxed">
              Regional reach, reliable supply. Moving organic rice and food grains across
              East Africa with trust and scale.
            </p>
          </div>

          <div>
            <h4 className="font-playfair font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/about"
                  className="text-cream-100 hover:text-primary-red transition-colors text-sm"
                >
                  About Revian
                </Link>
              </li>
              <li>
                <Link
                  to="/products"
                  className="text-cream-100 hover:text-primary-red transition-colors text-sm"
                >
                  Products
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="text-cream-100 hover:text-primary-red transition-colors text-sm"
                >
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to="/track-record"
                  className="text-cream-100 hover:text-primary-red transition-colors text-sm"
                >
                  Track Record
                </Link>
              </li>
              <li>
                <Link
                  to="/team"
                  className="text-cream-100 hover:text-primary-red transition-colors text-sm"
                >
                  Our Team
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-cream-100 hover:text-primary-red transition-colors text-sm"
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-playfair font-semibold text-lg mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary-red flex-shrink-0 mt-0.5" />
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-cream-100 hover:text-primary-red transition-colors text-sm"
                >
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-primary-red flex-shrink-0 mt-0.5" />
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="text-cream-100 hover:text-primary-red transition-colors text-sm"
                >
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary-red flex-shrink-0 mt-0.5" />
                <span className="text-cream-100 text-sm">
                  {contactInfo.address}
                </span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-playfair font-semibold text-lg mb-4">Follow Us</h4>
            <div className="flex gap-4">
              {contactInfo.socialLinks.map((link) => (
                <a
                  key={`${link.platform}-${link.url}`}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-cream-100/10 hover:bg-primary-red flex items-center justify-center transition-colors"
                  aria-label={link.platform.toUpperCase()}
                >
                  <img
                    src={socialIconSrc[link.platform] || socialIconSrc.instagram}
                    alt=""
                    className="w-5 h-5"
                    aria-hidden="true"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-cream-100/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-cream-100 text-sm">
              {currentYear} Revian Inc. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-cream-100 hover:text-primary-red transition-colors"
              >
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
